import React, { useRef, useEffect } from 'react';
import { Search, Plus, QrCode, Filter, CheckCircle2, RefreshCw, Users } from 'lucide-react';
import { useAlert } from '../../../context/AlertContext';

const FilterBar = ({
    searchTerm, setSearchTerm,
    onAddClick, onScannerOpen,
    filterMenuOpen, setFilterMenuOpen,
    selectedSegments, toggleSegment, setSelectedSegments,
    hasCreatePermission, t,
    onSyncAll,
    totalCount
}) => {
    const filterRef = useRef(null);
    const [isSyncing, setIsSyncing] = React.useState(false);
    const { showAlert } = useAlert();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterMenuOpen(false);
            }
        };

        const handleScroll = () => {
            if (filterMenuOpen) setFilterMenuOpen(false);
        };

        if (filterMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, { passive: true });
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [filterMenuOpen, setFilterMenuOpen]);

    const segments = [
        { id: 'new', label: 'Yeni Müşteriler', color: '#F5F5DC', description: 'İlk ziyaretini yapan' },
        { id: 'old', label: 'Eski Müşteriler', color: '#8B4513', description: 'Düzenli gelen' },
        { id: 'loyal', label: 'Sadık Üyeler', color: '#3E2723', description: 'Sık gelen müdavimler' },
        { id: 'risk', label: 'Riskli Üyeler', color: '#FF0000', description: 'Uzun süredir gelmeyen' }
    ];

    return (
        <div className="customers-filter-header" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <div className="search-container" style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        type="text"
                        placeholder={`${t('common.search')}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none' }}
                    />
                </div>
                {totalCount !== undefined && (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={14} /> {totalCount} sonuç
                    </span>
                )}
            </div>

            <div className="settings-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {hasCreatePermission && (
                    <button onClick={onAddClick} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '46px', padding: '0 24px', borderRadius: '12px', border: 'none' }}>
                        <Plus size={20} />
                        <span className="full-text">{t('customers.addCustomer')}</span>
                        <span className="short-text">Ekle</span>
                    </button>
                )}

                <button onClick={onScannerOpen} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', height: '46px', padding: '0 24px', borderRadius: '12px', cursor: 'pointer' }}>
                    <QrCode size={20} /> <span className="full-text">QR Tara</span><span className="short-text">Tara</span>
                </button>

                <div ref={filterRef} style={{ position: 'relative' }}>
                    <button
                        onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', height: '46px', padding: '0 24px', borderRadius: '12px',
                            background: filterMenuOpen || selectedSegments.length > 0 ? 'rgba(var(--primary-rgb), 0.1)' : 'var(--bg-card)',
                            border: `1px solid ${filterMenuOpen || selectedSegments.length > 0 ? 'var(--primary)' : 'var(--border)'}`,
                            color: filterMenuOpen || selectedSegments.length > 0 ? 'var(--primary)' : 'var(--text-main)',
                            fontWeight: 600, cursor: 'pointer', width: '100%'
                        }}
                    >
                        <Filter size={18} />
                        <span className="full-text">{selectedSegments.length > 0 ? `${selectedSegments.length} Filtre` : 'Filtrele'}</span>
                        <span className="short-text">Filtre</span>
                    </button>

                    {filterMenuOpen && (
                        <div className="card shadow-lg fade-in" style={{
                            position: 'absolute', top: '100%', right: 0, marginTop: '8px', zIndex: 100,
                            width: '300px', padding: '16px', background: 'var(--bg-card)',
                            border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1rem'
                        }}>
                            <div>
                                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Müşteri Segmentleri</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {segments.map(seg => (
                                        <button
                                            key={seg.id}
                                            onClick={() => toggleSegment(seg.id)}
                                            style={{
                                                padding: '10px 12px', borderRadius: '10px', border: 'none',
                                                background: selectedSegments.includes(seg.id) ? 'rgba(var(--primary-rgb), 0.1)' : 'transparent',
                                                color: selectedSegments.includes(seg.id) ? 'var(--primary)' : 'var(--text-main)',
                                                textAlign: 'left', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontSize: '0.875rem', fontWeight: selectedSegments.includes(seg.id) ? 700 : 500 }}>{seg.label}</div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '1px' }}>{seg.description}</div>
                                            </div>
                                            {selectedSegments.includes(seg.id) && <CheckCircle2 size={16} />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {selectedSegments.length > 0 && (
                                <button onClick={() => setSelectedSegments([])} style={{ width: '100%', marginTop: '4px', padding: '8px', border: 'none', background: 'transparent', color: 'var(--text-muted)', fontSize: '0.75rem', textDecoration: 'underline', cursor: 'pointer' }}>Filtreleri Temizle</button>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={async () => {
                        setIsSyncing(true);
                        try {
                            const count = await onSyncAll();
                            showAlert(`${count} müşteri güncellendi.`, 'success');
                        } catch (err) {
                            console.error(err);
                            showAlert("Hata oluştu.", 'error');
                        } finally {
                            setIsSyncing(false);
                        }
                    }}
                    disabled={isSyncing}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                        color: 'var(--text-main)', height: '46px', padding: '0 24px',
                        borderRadius: '12px', cursor: 'pointer', opacity: isSyncing ? 0.6 : 1,
                        transition: 'all 0.2s'
                    }}
                >
                    <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
                    <span className="full-text">Verileri Yenile</span>
                    <span className="short-text">Yenile</span>
                </button>
            </div>
            <style>{`
                @media (max-width: 768px) {
                    .customers-filter-header {
                        flex-direction: column-reverse !important;
                    }
                    .search-container {
                        width: 100% !important;
                        max-width: none !important;
                    }
                    .settings-header-actions {
                        width: 100% !important;
                        justify-content: space-between !important;
                        gap: 8px !important;
                    }
                    .settings-header-actions > *:not(style) {
                        flex: 1 !important;
                        justify-content: center !important;
                    }
                    .full-text { display: none !important; }
                    .short-text { display: inline !important; }
                }
                @media (min-width: 769px) {
                    .short-text { display: none !important; }
                }
            `}</style>
        </div>
    );
};

export default FilterBar;
